import { useState } from 'react';
import type { FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { phCapture } from '@/lib/analytics/posthog';
import type { SignupSource } from '@/lib/auth-types';
import { useAuthStore } from '@/stores/authStore';
import { STRINGS } from '@/strings/strings';

/**
 * SignupSourceCard — Marketing attribution package (2026-07-17), Part C.
 *
 * Renders a single dismissable card on WelcomeRoute asking the user how
 * they found Dreamable. The card is:
 *
 *   - Skippable via a "Später beantworten" ghost button — one tap dismisses.
 *   - Never blocking: WelcomeRoute's "Los geht's" CTA stays enabled
 *     regardless of the card state.
 *   - One-shot per session: on either submit or skip we set a sessionStorage
 *     flag so a re-mount of WelcomeRoute within the same session doesn't
 *     re-ask. Cross-session re-ask is prevented by the backend
 *     `user.signupSource !== null` check performed by the parent.
 *
 * On submit:
 *   - PATCH /api/users/me { signupSource } via the existing authStore
 *     updateProfile action.
 *   - phCapture('signup_source', { value }) — event-side attribution.
 *   - Set the sessionStorage flag and dismiss.
 *
 * On skip:
 *   - Set the sessionStorage flag and dismiss.
 *   - No PATCH, no PostHog event, no writes at all beyond the flag.
 */

export const SIGNUP_SOURCE_ASKED_STORAGE_KEY = 'dreamable_signup_source_asked';

// Enum values MUST match:
//   1. Backend Prisma `enum SignupSource` (prisma/schema.prisma).
//   2. `SignupSource` string union in lib/auth-types.ts.
//   3. PostHog `signup_source` event property values.
// Keep the four lists in lockstep. Order here drives the dropdown order
// users see — ai_assistant sits after google_search for GEO grouping,
// added 2026-07-17.
//
// `book_lightgate` (added 2026-08-06) is deliberately EXCLUDED — it's
// an implicit marker written by the LightGateOverlay at registration,
// never a self-report answer the customer picks. Narrowing the union
// via `Exclude` turns that semantic invariant into a compile-time
// proof: `STRINGS.onboarding.signupSource.options` is exhaustive over
// this narrower type without needing a `book_lightgate` label entry.
// Probecover Stage 1 — exclude the two IMPLICIT markers so the UI
// options list stays "discovery channels only". Both `book_lightgate`
// (set at book LightGate register) and `probecover` (set at /figuren
// LightGate register) are system-written, never customer-answered.
type UserSelectableSignupSource = Exclude<SignupSource, 'book_lightgate' | 'probecover'>;
const SIGNUP_SOURCE_OPTIONS: readonly UserSelectableSignupSource[] = [
  'instagram',
  'tiktok',
  'whatsapp_friends',
  'recommendation',
  'google_search',
  'ai_assistant',
  'press',
  'other',
] as const;

export function SignupSourceCard() {
  const updateProfile = useAuthStore((s) => s.updateProfile);
  const [selected, setSelected] = useState<UserSelectableSignupSource | ''>('');
  const [dismissed, setDismissed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Card removes itself from the DOM after the first submit or skip within
  // the session — parallels the sessionStorage flag so a same-tick re-render
  // doesn't flash the card back in before the parent's storage-read gate
  // catches up on next mount.
  if (dismissed) return null;

  const markAsked = () => {
    try {
      window.sessionStorage.setItem(SIGNUP_SOURCE_ASKED_STORAGE_KEY, '1');
    } catch {
      // Safari Private Mode / SSR — dismiss client-side either way.
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    setSubmitting(true);
    try {
      // Fire the event before the PATCH so the funnel event still lands
      // when the server call fails (attribution should be event-side robust,
      // not gated by a possibly-flaky PATCH).
      phCapture('signup_source', { value: selected });
      await updateProfile({ signupSource: selected });
    } catch {
      // Attribution isn't a blocking flow — swallow, mark-asked, dismiss.
      // The user isn't shown a re-try affordance; a second signup or a
      // manual PATCH from a future settings screen can correct.
    } finally {
      markAsked();
      setDismissed(true);
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    // Skip = mark-asked + dismiss ONLY. No PATCH, no PostHog event, no
    // network at all. Spec covers this end-to-end (Part C skip path).
    markAsked();
    setDismissed(true);
  };

  return (
    <div
      className="mb-6 rounded-lg border border-dream-white/15 bg-dream-white/5 p-4 text-left"
      data-testid="signup-source-card"
    >
      <h2 className="text-sm font-semibold text-dream-white mb-1">
        {STRINGS.onboarding.signupSource.title}
      </h2>
      <p className="text-xs text-dream-white/70 mb-3">{STRINGS.onboarding.signupSource.helper}</p>

      <form onSubmit={handleSubmit} className="space-y-3">
        <label className="block">
          <span className="sr-only">{STRINGS.onboarding.signupSource.title}</span>
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value as UserSelectableSignupSource | '')}
            aria-label={STRINGS.onboarding.signupSource.title}
            className="w-full h-10 rounded-md border border-dream-white/20 bg-dream-white text-dream-navy px-3 text-sm"
          >
            <option value="" disabled>
              {STRINGS.onboarding.signupSource.selectPlaceholder}
            </option>
            {SIGNUP_SOURCE_OPTIONS.map((value) => (
              <option key={value} value={value}>
                {STRINGS.onboarding.signupSource.options[value]}
              </option>
            ))}
          </select>
        </label>

        <div className="flex gap-2">
          <Button
            type="submit"
            disabled={!selected || submitting}
            aria-disabled={!selected || submitting}
            className="flex-1 h-10 text-sm font-semibold"
          >
            {STRINGS.onboarding.signupSource.submit}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={handleSkip}
            disabled={submitting}
            className="h-10 text-sm text-dream-white/70 hover:text-dream-white"
          >
            {STRINGS.onboarding.signupSource.skip}
          </Button>
        </div>
      </form>
    </div>
  );
}
